import type { AxiosError } from 'axios';
import { getApiErrorRequestId } from './apiErrorUtils';
import { extractHttpStatus, hasResponse } from './errors';
import type { ToastInput } from '../contexts/ToastContext';
import {
  DEFAULT_USER_FACING_ERROR_MESSAGE,
  toUserFacingErrorMessage,
} from './userFacingErrorMessage';

function headerValue(
  headers: Record<string, unknown> | undefined,
  name: string,
): string | undefined {
  if (!headers) return undefined;
  const lower = name.toLowerCase();
  for (const key of Object.keys(headers)) {
    if (key.toLowerCase() === lower) {
      const v = headers[key];
      if (typeof v === 'string' && v.trim()) return v.trim();
      if (Array.isArray(v) && typeof v[0] === 'string' && v[0].trim()) return v[0].trim();
    }
  }
  return undefined;
}

/**
 * Attach server request ID to an axios error so callers can read error.supportRequestId.
 */
export function attachSupportRequestIdToAxiosError(error: AxiosError): void {
  if (error.supportRequestId) return;
  const headers = error.response?.headers as Record<string, unknown> | undefined;
  const fromHeader = headerValue(headers, 'x-request-id') || headerValue(headers, 'X-Request-ID');
  const fromBody =
    typeof error.response?.data === 'object' && error.response?.data !== null
      ? getApiErrorRequestId(error.response.data)
      : undefined;
  const id = fromHeader || fromBody;
  if (id) error.supportRequestId = id;
}

export function extractSupportRequestIdFromAxiosError(error: unknown): string | undefined {
  if (error && typeof error === 'object' && 'supportRequestId' in error) {
    const id = (error as { supportRequestId?: unknown }).supportRequestId;
    if (typeof id === 'string' && id.trim()) return id.trim();
  }
  if (!hasResponse(error)) return undefined;
  const data = error.response.data;
  const fromBody = getApiErrorRequestId(data);
  if (fromBody) return fromBody;
  const responseRecord = error.response as {
    headers?: Record<string, unknown>;
  };
  const headers = responseRecord.headers;
  return headerValue(headers, 'x-request-id') || headerValue(headers, 'X-Request-ID');
}

export function appendSupportReferenceToMessage(
  userMessage: string,
  requestId: string | undefined,
): string {
  if (!requestId) return userMessage;
  return `${userMessage}\n\nSupport reference: ${requestId}`;
}

export interface SupportBundleParams {
  flowLabel: string;
  userMessage: string;
  requestId?: string;
  httpStatus?: number;
  path?: string;
}

/**
 * Plain-text blob users can paste into enterprise support tickets.
 */
export function buildSupportBundleText(params: SupportBundleParams): string {
  const lines = [
    `Flow: ${params.flowLabel}`,
    `Time (client): ${new Date().toISOString()}`,
    ...(params.httpStatus != null ? [`HTTP status: ${params.httpStatus}`] : []),
    ...(params.path ? [`Path: ${params.path}`] : []),
    `Summary: ${params.userMessage}`,
    ...(params.requestId ? [`Request ID: ${params.requestId}`] : []),
  ];
  return lines.join('\n');
}

export async function copySupportBundleToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  } catch {
    return false;
  }
}

type ShowToastFn = (message: ToastInput) => void;

/**
 * Show an error toast with optional "Copy to clipboard" action when a request ID is known.
 */
export function showSupportAwareErrorToast(
  showToast: ShowToastFn,
  flowLabel: string,
  userMessage: string,
  error: unknown,
  extra?: { fallbackRequestId?: string | null },
): void {
  const safeUserMessage = toUserFacingErrorMessage(
    userMessage.trim() || DEFAULT_USER_FACING_ERROR_MESSAGE,
    DEFAULT_USER_FACING_ERROR_MESSAGE,
  );
  const trimmedFallback = extra?.fallbackRequestId?.trim();
  const requestId =
    extractSupportRequestIdFromAxiosError(error) ||
    (trimmedFallback && trimmedFallback.length > 0 ? trimmedFallback : undefined);
  const status = extractHttpStatus(error);
  let path: string | undefined;
  if (error && typeof error === 'object' && 'config' in error) {
    const url = (error as { config?: { url?: string } }).config?.url;
    if (typeof url === 'string') path = url;
  }
  const message = appendSupportReferenceToMessage(safeUserMessage, requestId);
  const bundle = requestId
    ? buildSupportBundleText({
        flowLabel,
        userMessage: safeUserMessage,
        requestId,
        httpStatus: status,
        path,
      })
    : undefined;

  showToast({
    type: 'error',
    message,
    duration: requestId ? 14_000 : 10_000,
    ...(bundle
      ? {
          action: {
            label: 'Copy to clipboard',
            onClick: () => {
              void copySupportBundleToClipboard(bundle).then((ok) => {
                if (ok) return;
                showToast({
                  type: 'error',
                  message:
                    'Could not copy to clipboard. Select and copy this text for support:\n\n' +
                    bundle,
                  duration: 45_000,
                });
              });
            },
          },
        }
      : {}),
  });
}
